import React, { useState, useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { toDateKey } from '@/utils/date-key';

interface RecallReportCalendarProps {
  availableDates: string[]; // Array of 'YYYY-MM-DD' strings that have reports
  selectedDate: string | null; // Currently selected date ('YYYY-MM-DD') or null for today
  onDateSelect: (date: string | null) => void; // null means "go to today"
  hasTodayReport: boolean; // Whether today's report has arrived
}

const DAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const RecallReportCalendar: React.FC<RecallReportCalendarProps> = ({
  availableDates,
  selectedDate,
  onDateSelect,
  hasTodayReport,
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => new Date());

  const todayKey = useMemo(() => toDateKey(new Date()), []);

  const monthLabel = useMemo(() => {
    return currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [currentMonth]);

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Monday-based start: getDay() returns 0=Sun,1=Mon,...,6=Sat
    // Convert to Mon=0, Tue=1, ..., Sun=6
    const rawDow = firstDay.getDay();
    const startOffset = rawDow === 0 ? 6 : rawDow - 1; // days to go back to reach Monday

    const days: Array<{
      date: number;
      dateKey: string;
      isCurrentMonth: boolean;
      hasReport: boolean;
      isSelected: boolean;
      isToday: boolean;
    }> = [];

    const pushDay = (date: number, d: Date, isCurrentMonth: boolean) => {
      const dateKey = toDateKey(d);
      days.push({
        date,
        dateKey,
        isCurrentMonth,
        hasReport: availableDates.includes(dateKey),
        isSelected: dateKey === selectedDate || (selectedDate === null && dateKey === todayKey),
        isToday: dateKey === todayKey,
      });
    };

    // Previous month padding
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startOffset - 1; i >= 0; i--) {
      const date = prevMonthLastDay - i;
      const prevMonth = month === 0 ? 11 : month - 1;
      const prevYear = month === 0 ? year - 1 : year;
      pushDay(date, new Date(prevYear, prevMonth, date), false);
    }

    // Current month days
    const daysInMonth = lastDay.getDate();
    for (let date = 1; date <= daysInMonth; date++) {
      pushDay(date, new Date(year, month, date), true);
    }

    // Next month padding to reach 42 cells (6 rows x 7 cols)
    const remainingDays = 42 - days.length;
    for (let date = 1; date <= remainingDays; date++) {
      const nextMonth = month === 11 ? 0 : month + 1;
      const nextYear = month === 11 ? year + 1 : year;
      pushDay(date, new Date(nextYear, nextMonth, date), false);
    }

    return days;
  }, [currentMonth, availableDates, selectedDate, todayKey]);

  const goToPreviousMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
    );
  };

  const goToNextMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
    );
  };

  const handleDayClick = (day: (typeof calendarDays)[number]) => {
    if (!day.hasReport) return;
    if (day.isToday) {
      onDateSelect(null); // "today" behavior
    } else {
      onDateSelect(day.dateKey);
    }
  };

  return (
    <div>
      {/* Month navigation header */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={goToPreviousMonth}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
        </button>
        <h3 className="text-sm font-semibold text-gray-900">{monthLabel}</h3>
        <button
          type="button"
          onClick={goToNextMonth}
          className="p-1 hover:bg-gray-100 rounded"
        >
          <ChevronRightIcon className="h-4 w-4 text-gray-600" />
        </button>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 gap-0 mb-1">
        {DAY_HEADERS.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-gray-500 py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-0">
        {calendarDays.map((day, index) => {
          const isClickable = day.hasReport;

          return (
            <button
              key={index}
              type="button"
              onClick={() => handleDayClick(day)}
              disabled={!isClickable}
              className={clsx(
                'flex items-center justify-center w-8 h-8 text-xs rounded-full transition-colors mx-auto',
                // Selected date: filled blue
                day.isSelected &&
                  day.hasReport &&
                  'bg-blue-600 text-white font-bold',
                // Today ring (when not selected)
                day.isToday &&
                  !day.isSelected &&
                  'ring-1 ring-blue-400',
                // Available date (not selected): bold, clickable
                !day.isSelected &&
                  day.hasReport &&
                  day.isCurrentMonth &&
                  'font-bold text-gray-900 hover:bg-gray-100 cursor-pointer',
                !day.isSelected &&
                  day.hasReport &&
                  !day.isCurrentMonth &&
                  'font-bold text-gray-500 hover:bg-gray-100 cursor-pointer',
                // Unavailable date: grey, not clickable
                !day.hasReport &&
                  day.isCurrentMonth &&
                  'text-gray-300 cursor-default font-normal',
                !day.hasReport &&
                  !day.isCurrentMonth &&
                  'text-gray-200 cursor-default font-normal',
              )}
            >
              {day.date}
            </button>
          );
        })}
      </div>

      {/* FTP status indicator */}
      {hasTodayReport ? (
        <p className="text-xs text-green-600 mt-2">FTP data received</p>
      ) : (
        <p className="text-xs text-amber-600 mt-2">Waiting for FTP data...</p>
      )}
    </div>
  );
};
